import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, getProfile, logout, isAuthenticated, setStoredToken } from '../services/authService';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (user: User, token: string) => void;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true; // Flag to prevent state updates if component unmounts
    
    const initializeAuth = async () => {
      try {
        console.log('AuthContext: Initializing auth...');
        if (isAuthenticated()) {
          console.log('AuthContext: Token found, getting profile...');
          const profile = await getProfile();
          if (isMounted) {
            console.log('AuthContext: Profile loaded:', profile);
            setUser(profile);
          }
        } else {
          console.log('AuthContext: No token found');
          if (isMounted) {
            setUser(null);
          }
        }
      } catch (err) {
        console.error('AuthContext: Failed to load user profile:', err);
        if (isMounted) {
          // Don't set error for initial load failure - user might not be logged in
          await logout(); // Clear invalid token
          setUser(null);
        }
      } finally {
        if (isMounted) {
          console.log('AuthContext: Setting loading to false');
          setLoading(false);
        }
      }
    };

    initializeAuth();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, []);

  // Debug effect to log user state changes
  useEffect(() => {
    console.log('AuthContext user state changed:', user);
  }, [user]);

  const handleLogin = (user: User, token: string) => {
    console.log('AuthContext handleLogin called with:', user, token);
    setStoredToken(token);
    setUser(user);
    setError(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      setUser(null);
      setError(null);
    } catch (err) {
      console.error('Logout error:', err);
      // Still clear user even if logout fails
      setUser(null);
    }
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  const clearError = () => {
    setError(null);
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    login: handleLogin,
    logout: handleLogout,
    updateUser,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
